import { cva, type VariantProps } from 'class-variance-authority'

/**
 * Skeleton component style variants
 *
 * Base element (shared):
 * - skeletonVariants: Pulsing bar with size/width options
 *
 * ProductView containers (frontend-specific):
 * - skeletonSectionVariants: Section-specific layout containers
 * - Helper variants for inner layout elements
 *
 * Design tokens from tailwind.config.js:
 * - cards-bg (#F5F5F5) - Section background
 * - muted (hsl CSS var) - Skeleton pulse color
 */

// =============================================================================
// BASE SKELETON ELEMENT (shared)
// =============================================================================

export const skeletonVariants = cva(
  'animate-pulse rounded bg-muted',
  {
    variants: {
      size: {
        default: '',
        sm: 'h-4 xl:h-6',
        md: 'h-5 xl:h-6',
        lg: 'h-6 xl:h-8',
        xl: 'h-11 xl:h-12',
        tab: 'h-[50px]',
        content: 'h-[500px]',
        fill: 'h-full',
      },
      width: {
        default: '',
        full: 'w-full',
        half: 'w-1/2',
        threeQuarters: 'w-3/4',
        twoSixths: 'w-2/6',
        threeSixths: 'w-3/6',
      },
    },
    defaultVariants: {
      size: 'default',
      width: 'default',
    },
  }
)

export type SkeletonVariants = VariantProps<typeof skeletonVariants>

// =============================================================================
// PRODUCT VIEW SKELETON SECTIONS (frontend-specific)
// =============================================================================

/**
 * Section container variants for ProductView skeleton loading states.
 * Each section has a unique layout for its placeholder UI.
 */
export const skeletonSectionVariants = cva(
  '',
  {
    variants: {
      section: {
        pageTitle: 'w-full flex flex-col justify-start gap-3 mb-3 xl:mb-5 bg-cards-bg',
        priceInfo: 'flex flex-col justify-center items-center md:items-start xl:justify-start',
        addToCart: 'flex flex-row items-start justify-center xl:justify-start mb-3 xl:mb-5 gap-3',
        infoLine: 'w-full justify-center xl:justify-start gap-3 mb-3 xl:mb-5 bg-cards-bg',
        slider: 'flex flex-col gap-3 bg-cards-bg mb-3 mt-3 xl:mb-5 h-[380px] xl:h-[470px]',
        details: 'h-[550px] flex flex-col items-start justify-center xl:justify-start mb-3 mt-3 xl:mb-5 gap-3 bg-cards-bg',
        tabContent: 'flex flex-col gap-3 bg-cards-bg xl:mb-5 mb-2 h-[550px]',
        description: 'flex flex-col gap-3 bg-cards-bg xl:mb-5 mb-2 mt-5 h-[550px]',
      },
    },
  }
)

export type SkeletonSectionVariants = VariantProps<typeof skeletonSectionVariants>

// =============================================================================
// PRODUCT VIEW SKELETON HELPERS (frontend-specific)
// =============================================================================

/** Slider skeleton grid - horizontal row of skeleton cards */
export const skeletonSliderGridVariants = cva([
  'flex flex-row gap-1 mb-3 mt-3 w-full flex-1',
])

/** Slider skeleton grid item - individual skeleton card slot */
export const skeletonSliderItemVariants = cva([
  'flex-1 bg-white mx-1',
])

/** Details skeleton inner row */
export const skeletonDetailsRowVariants = cva([
  'flex flex-row items-start justify-center xl:justify-start mb-3 mt-3 w-full',
])

/** Content area for tab-like sections (AdditionalInfo, Description) */
export const skeletonContentAreaVariants = cva([
  'flex-1 bg-white p-3',
])
